import { ctaClasses, type CtaStyleProps } from './cta-classes';

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

/**
 * Bedrock CTAs — the marketing site's one button system.
 *
 * ## The brand shift this encodes
 *
 * The primary action is now an INK fill (`#0c0d10`), not the brand gradient
 * the site previously used for every CTA. That is deliberate and comes from
 * the design: with the page turned light, the gradient is reserved for the
 * things that ARE the brand — the logo, the one emphasised phrase in a
 * headline, and the demo chrome. Spending it on every button flattened it into
 * decoration. A near-black fill is also the highest-contrast affordance
 * available on a white page (19.35:1), which is the point of a primary action.
 *
 * ## Tone
 *
 * `onDark` inverts the pair for a dark band: primary becomes a white fill with
 * ink text, secondary a hairline outline in white. Explicit rather than
 * inherited, for the same reason the typography tone is — a dark band can
 * contain a light card.
 *
 * ## Polymorphism
 *
 * Explicit and typed: `<Cta>` renders a `<button>`, `<CtaLink>` an `<a>`. For
 * a react-router `<Link>`, spread `ctaClasses(...)` — it is a pure function of
 * the props.
 *
 * This IS the site's one button system. `marketing-ui/Button` was a second,
 * near-identical set with a 6px radius against this one's 10–12px and a
 * coarse-pointer-only touch target, so a pricing-card button rendered visibly
 * unlike the header CTA beside it. Its eight call sites moved here and it was
 * deleted.
 *
 * That paragraph used to end "and the claim is now true rather than
 * aspirational", and it was wrong: two hand-rolled buttons never moved with
 * the eight. The featured PRICING tier's buy button kept a 6px radius, its own
 * padding, `opacity-90` for a hover and no press or disabled state — and,
 * because `bg-primary` is repointed to `--bd-blue` for this app, a BLUE fill,
 * which is the one thing the paragraph above says a primary action is not. The
 * DSAR submit had its own set: a 4px radius, a 44px floor that applied only to
 * touch, the UA's default focus ring instead of the house one, and no hover cue
 * at all — measured, not inferred.
 *
 * Both are off `ctaClasses` now. `cta-states.test.ts` pins the four states each
 * variant must answer, and the two components' own tests pin that they take
 * them from here — because a doc comment asserting a property nothing checks is
 * exactly how the previous claim came to be false.
 */

interface CtaCommon extends CtaStyleProps {
  readonly children: ReactNode;
}

export type CtaProps = CtaCommon &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export function Cta({
  variant,
  size,
  onDark,
  className,
  children,
  type = 'button',
  ...rest
}: CtaProps): ReactElement {
  return (
    <button type={type} className={ctaClasses({ variant, size, onDark, className })} {...rest}>
      {children}
    </button>
  );
}

export type CtaLinkProps = CtaCommon &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'>;

export function CtaLink({
  variant,
  size,
  onDark,
  className,
  children,
  ...rest
}: CtaLinkProps): ReactElement {
  return (
    <a className={ctaClasses({ variant, size, onDark, className })} {...rest}>
      {children}
    </a>
  );
}

/** Which way the CTA goes. `back` is the return-to-safety arrow on 404/error. */
export type CtaArrowDirection = 'forward' | 'back';

/**
 * The arrow on a CTA. A separate component because it must be `aria-hidden` (it
 * is punctuation, not content) and must MIRROR under RTL — an arrow pointing
 * right in a right-to-left page points backwards.
 *
 * ## One arrow, not three
 *
 * The same role had three implementations. Four CTAs used a lucide
 * `<ArrowRight className="h-4 w-4 rtl:rotate-180" />` — a 2px-stroke icon; two
 * used this component's typographic glyph; and the 404 and error pages
 * hand-rolled their own `<span aria-hidden className="rtl:-scale-x-100">←`,
 * which is this component with a different arrow in it. An icon and a glyph do
 * not match: the glyph inherits the label's size and weight, the icon does not,
 * so the same button rendered differently on `/features` than on the landing.
 *
 * The glyph wins rather than the icon, and the reason is not the head count
 * (it was four each). It inherits — one arrow that is always exactly as heavy
 * as the text beside it, at every size, in every locale — and it needs no size
 * class, so there is nothing for a call site to get wrong.
 *
 * MIRRORED rather than rotated, which the icon sites did. For a horizontal
 * arrow the two coincide, so nothing was visibly wrong; they stop coinciding
 * the moment anyone reaches for a diagonal (`ArrowUpRight` rotated 180° points
 * DOWN-left, not up-left). Mirroring is what "the reading direction flipped"
 * actually means, so it is the operation that keeps being right.
 */
export function CtaArrow({
  direction = 'forward',
}: { readonly direction?: CtaArrowDirection } = {}): ReactElement {
  return (
    <span aria-hidden="true" className="rtl:-scale-x-100">
      {direction === 'back' ? '←' : '→'}
    </span>
  );
}
